import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import {
  useSellerOrderApprovalSettings,
  useSellerOrders,
  useSellerLiveInvalidation,
} from '../hooks/queries/sellerQueries';
import {
  ShoppingCart,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Loader2,
  Search,
} from 'lucide-react';

const PAGE_SIZE = 20;

interface OrderItem {
  id?: string;
  product: {
    name: string;
    images?: string[];
  };
  quantity: number;
  price: number;
  sku?: string;
  size?: string;
  color?: string;
}

interface Order {
  id: string;
  orderNumber: string;
  orderStatus: string;
  paymentStatus?: string;
  totalAmount: number;
  sellerAmount?: number;
  orderTotalAmount?: number;
  isMultiVendor?: boolean;
  items: OrderItem[];
  user: {
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
  createdAt: string;
}

type StatusCounts = Record<string, number>;

const Orders: React.FC = () => {
  const { t } = useTranslation(['orders', 'common']);
  const { invalidateOrders } = useSellerLiveInvalidation();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionId, setActionId] = useState<string | null>(null);

  const { data: approvalData } = useSellerOrderApprovalSettings();
  const sellerCanApproveOrders = Boolean(approvalData);

  const { data: ordersData, isLoading } = useSellerOrders({
    page,
    limit: PAGE_SIZE,
    status: statusFilter || undefined,
    search: searchQuery || undefined,
  });

  const orders = (ordersData?.orders || []) as Order[];
  const totalPages = ordersData?.totalPages ?? 1;
  const total = ordersData?.total ?? 0;
  const statusCounts = (ordersData?.statusCounts || { all: 0 }) as StatusCounts;

  const statusOptions = [
    { value: '', label: t('filters.allStatus'), countKey: 'all' },
    { value: 'PENDING', label: t('filters.pending'), countKey: 'PENDING' },
    { value: 'OUT_FOR_DELIVERY', label: t('filters.shipped'), countKey: 'OUT_FOR_DELIVERY' },
    { value: 'COMPLETE', label: t('filters.delivered'), countKey: 'COMPLETE' },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const handleAcceptOrder = async (orderId: string) => {
    setActionId(orderId);
    try {
      await api.put(`/seller/orders/${orderId}/confirm`, {});
      await invalidateOrders();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('messages.actionFailed'));
    } finally {
      setActionId(null);
    }
  };

  const handleRejectOrder = async (orderId: string) => {
    const reason = window.prompt(t('messages.rejectReasonPrompt'));
    if (reason === null) return;

    setActionId(orderId);
    try {
      await api.put(`/seller/orders/${orderId}/reject`, { reason: reason || undefined });
      await invalidateOrders();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('messages.actionFailed'));
    } finally {
      setActionId(null);
    }
  };

  const getStatusCount = (countKey: string) => statusCounts[countKey] ?? 0;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETE':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'CANCELLED':
        return <XCircle className="text-red-500" size={20} />;
      case 'SHIPPED':
        return <Truck className="text-blue-500" size={20} />;
      default:
        return <Package className="text-yellow-500" size={20} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETE':
        return 'badge-green';
      case 'CANCELLED':
        return 'badge-red';
      case 'SHIPPED':
        return 'badge-gray';
      default:
        return 'badge-yellow';
    }
  };

  const getPaymentStatusText = (paymentStatus?: string, orderStatus?: string) => {
    if (paymentStatus === 'COMPLETED') {
      return t('paymentStatus.COMPLETED');
    }

    if (orderStatus === 'COMPLETE' && paymentStatus !== 'COMPLETED') {
      return t('paymentStatus.AWAITING_CASH', { defaultValue: 'Awaiting cash collection' });
    }

    switch (paymentStatus) {
      case 'COMPLETED':
        return t('paymentStatus.COMPLETED');
      case 'PENDING':
        return t('paymentStatus.PENDING');
      case 'PROCESSING':
        return t('paymentStatus.PROCESSING');
      case 'FAILED':
        return t('paymentStatus.FAILED');
      case 'REFUNDED':
        return t('paymentStatus.REFUNDED');
      default:
        return t('paymentStatus.PENDING');
    }
  };

  const getPaymentStatusColor = (paymentStatus?: string, orderStatus?: string) => {
    if (paymentStatus === 'COMPLETED') {
      return 'badge-green';
    }

    if (orderStatus === 'COMPLETE') {
      return 'badge-yellow';
    }

    switch (paymentStatus) {
      case 'COMPLETED':
        return 'badge-green';
      case 'PENDING':
        return 'badge-yellow';
      case 'PROCESSING':
        return 'badge-blue';
      case 'FAILED':
        return 'badge-red';
      case 'REFUNDED':
        return 'badge-gray';
      default:
        return 'badge-yellow';
    }
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  const hasActiveFilters = Boolean(statusFilter || searchQuery);

  return (
    <div className="space-y-4 md:space-y-6 max-w-full overflow-x-hidden">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">{t('subtitle')}</p>
        </div>
      </div>

      <div className="card p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative md:col-span-2">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder={t('search')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              setSearchQuery('');
              setStatusFilter('');
              setPage(1);
            }}
            disabled={!hasActiveFilters && !searchInput}
            className="btn-outline disabled:opacity-50"
          >
            {t('buttons.clearFilters', { ns: 'common' })}
          </button>
        </div>
        {searchInput && searchInput !== searchQuery && (
          <p className="text-xs text-gray-500 mt-2">{t('messages.searching')}</p>
        )}
      </div>

      <div className="flex flex-wrap gap-2 md:gap-3">
        {statusOptions.map((opt) => {
          const isActive = statusFilter === opt.value;
          const count = getStatusCount(opt.countKey);

          return (
            <button
              key={opt.value || 'all-status'}
              type="button"
              onClick={() => {
                setStatusFilter(opt.value);
                setPage(1);
              }}
              className={`px-4 py-2 md:px-6 md:py-3 rounded-lg font-medium text-sm md:text-base transition-colors ${
                isActive
                  ? 'bg-custom-primary text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {opt.label} {count > 0 && `(${count})`}
            </button>
          );
        })}
      </div>

      {isLoading && !ordersData ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-primary" />
        </div>
      ) : orders.length === 0 ? (
        <div className="card p-12 text-center">
          <ShoppingCart size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">
            {hasActiveFilters
              ? t('messages.noOrdersMatchingFilters')
              : t('messages.noOrdersFound')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const isPending = order.orderStatus === 'PENDING';
            const isProcessing = actionId === order.id;

            return (
              <div key={order.id} className="card p-4 md:p-6 overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base md:text-lg text-gray-900 truncate">
                      {t('fields.orderNumber')} #{order.orderNumber}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-500 truncate max-w-full">
                      {order.user
                        ? `${order.user.firstName || ''} ${order.user.lastName || ''}`.trim() ||
                          order.user.email ||
                          t('fields.customer')
                        : t('fields.customer')}
                      {order.user?.email && ` (${order.user.email})`}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 flex-shrink-0">
                    <div className="flex flex-col items-start sm:items-center">
                      <span className="text-xs text-gray-500 mb-1 hidden sm:block">
                        {t('fields.orderStatusLabel')}
                      </span>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(order.orderStatus)}
                        <span className={getStatusColor(order.orderStatus)}>
                          {t(`status.${order.orderStatus}`, { defaultValue: order.orderStatus })}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col items-start sm:items-center">
                      <span className="text-xs text-gray-500 mb-1 hidden sm:block">
                        {t('fields.paymentStatusLabel')}
                      </span>
                      <span
                        className={getPaymentStatusColor(order.paymentStatus, order.orderStatus)}
                      >
                        {getPaymentStatusText(order.paymentStatus, order.orderStatus)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4 mt-4">
                  {order.items && order.items.length > 0 ? (
                    order.items.map((item, itemIndex) => {
                      const itemKey = item.id || `${order.id}-item-${itemIndex}`;

                      return (
                        <div key={itemKey} className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            {item.product?.images?.[0] && (
                              <img
                                src={item.product.images[0]}
                                alt={item.product.name || 'Product'}
                                className="w-12 h-12 object-cover rounded"
                              />
                            )}
                            <div>
                              <p className="font-medium text-gray-900">
                                {item.product?.name ||
                                  t('fields.product', { defaultValue: 'Product' })}
                              </p>
                              <p className="text-sm text-gray-500">
                                {t('fields.quantity')}: {item.quantity || 0}
                              </p>
                              {(item.size || item.color) && (
                                <p className="text-xs text-gray-500">
                                  {item.size &&
                                    `${t('fields.size', { defaultValue: 'Size' })}: ${item.size}`}
                                  {item.size && item.color && ' • '}
                                  {item.color &&
                                    `${t('fields.color', { defaultValue: 'Color' })}: ${item.color}`}
                                </p>
                              )}
                            </div>
                          </div>
                          <p className="font-semibold text-gray-900 flex-shrink-0 ml-2">
                            ${((item.price || 0) * (item.quantity || 0)).toFixed(2)}
                          </p>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-500">
                      {t('messages.noItems', { defaultValue: 'No items in this order' })}
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs md:text-sm text-gray-500">
                    {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="text-right sm:text-left">
                      <p className="text-base md:text-lg font-bold text-gray-900">
                        {t('fields.yourTotal')}: $
                        {(order.sellerAmount ?? order.totalAmount ?? 0).toFixed(2)}
                      </p>
                      {order.isMultiVendor && order.orderTotalAmount != null && (
                        <p className="text-xs text-gray-500">
                          {t('messages.multiVendorOrder', {
                            amount: order.orderTotalAmount.toFixed(2),
                          })}
                        </p>
                      )}
                    </div>
                    {isPending && sellerCanApproveOrders && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={isProcessing}
                          onClick={() => handleAcceptOrder(order.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {isProcessing ? (
                            <Loader2 className="animate-spin" size={16} />
                          ) : (
                            <CheckCircle size={16} />
                          )}
                          {t('actions.accept')}
                        </button>
                        <button
                          type="button"
                          disabled={isProcessing}
                          onClick={() => handleRejectOrder(order.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {isProcessing ? (
                            <Loader2 className="animate-spin" size={16} />
                          ) : (
                            <XCircle size={16} />
                          )}
                          {t('actions.reject')}
                        </button>
                      </div>
                    )}
                    {isPending && !sellerCanApproveOrders && (
                      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        {t('messages.waitingAdminValidation')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isLoading && total > 0 && (
        <div className="card p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-500">
              {t('table.showing', { ns: 'common' })} {rangeStart} {t('table.to', { ns: 'common' })}{' '}
              {rangeEnd} {t('table.of', { ns: 'common' })} {total}{' '}
              {t('table.results', { ns: 'common' })}
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-outline text-sm py-1 px-2 disabled:opacity-50"
              >
                {t('table.previous', { ns: 'common' })}
              </button>
              <span className="text-sm text-gray-700">
                {t('table.page', { ns: 'common' })} {page} {t('table.of', { ns: 'common' })}{' '}
                {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="btn-outline text-sm py-1 px-2 disabled:opacity-50"
              >
                {t('table.next', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
